/**
 * APIClient.js - Trading Card API Client (Frontend Middleware edition)
 *
 * Talks directly to your API server (non–App Proxy).
 * - Fetches a short-lived token from /api/frontend/token and sends it as `x-frontend-token`
 * - Optionally includes x-api-key (or Bearer) for protected POST routes (prod)
 *
 * Usage:
 *   const api = new APIClient({
 *     baseUrl: 'https://YOUR_API_DOMAIN', // <-- required
 *     apiKey: null, // or 'key1' in prod if you choose to expose a limited-scope key
 *   });
 */

class APIClient {
    constructor(config = {}) {
        if (!config.baseUrl) {
            console.warn("[APIClient] baseUrl is required, e.g., https://YOUR_API_DOMAIN");
        }
        this.config = {
            baseUrl: (config.baseUrl || '').replace(/\/+$/, ''), // no trailing slash
            apiKey: config.apiKey || null,                       // optional (prod-only)
            timeout: config.timeout || 30000,
            retries: config.retries || 3,
            retryDelay: config.retryDelay || 1000,
            tokenEndpoint: config.tokenEndpoint || '/api/frontend/token',
            // how long before expiry to refresh (in seconds)
            tokenSkewSeconds: config.tokenSkewSeconds || 20,
            ...config
        };

        // token cache
        this._token = null;
        this._tokenExp = 0; // epoch secs
        this._tokenPromise = null; // in-flight promise to avoid races
    }

    // -------- URL helpers --------
    buildUrl(endpoint) {
        const base = this.config.baseUrl;
        const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        return `${base}${path}`;
    }

    // -------- Frontend token management --------
    async ensureToken() {
        const now = Math.floor(Date.now() / 1000);
        if (this._token && now < (this._tokenExp - this.config.tokenSkewSeconds)) {
            return this._token;
        }
        // De-duplicate concurrent fetches
        if (this._tokenPromise) return this._tokenPromise;

        this._tokenPromise = (async () => {
            const url = this.buildUrl(this.config.tokenEndpoint);
            const r = await fetch(url, { method: 'GET', credentials: 'include' });
            if (!r.ok) {
                this._tokenPromise = null;
                throw new Error(`Failed to fetch frontend token (${r.status})`);
            }
            const data = await r.json();
            const token = data?.token;
            const expiresIn = Number(data?.expiresIn || 0);
            if (!token || !expiresIn) {
                this._tokenPromise = null;
                throw new Error('Invalid token response');
            }
            this._token = token;
            this._tokenExp = Math.floor(Date.now() / 1000) + expiresIn;
            this._tokenPromise = null;
            return token;
        })();

        return this._tokenPromise;
    }

    // -------- Core request --------
    async request(endpoint, options = {}) {
        const url = this.buildUrl(endpoint);

        const headers = { ...(options.headers || {}) };
        if (this.config.apiKey && !headers['x-api-key'] && !headers['Authorization']) {
            headers['x-api-key'] = this.config.apiKey;
        }

        // Prepare request options
        const requestOptions = {
            method: options.method || 'GET',
            headers,
            credentials: 'include', // keep cookies/session if you add them later
            ...options,
            signal: options.signal
        };

        // If body is plain object (not FormData/string), send JSON
        if (requestOptions.body && !(requestOptions.body instanceof FormData) && typeof requestOptions.body !== 'string') {
            headers['Content-Type'] = headers['Content-Type'] || 'application/json';
            requestOptions.body = JSON.stringify(requestOptions.body);
        }

        // FormData: let browser set content-type boundary
        if (requestOptions.body instanceof FormData) {
            delete headers['Content-Type'];
        }

        let lastError;

        for (let attempt = 0; attempt <= this.config.retries; attempt++) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

                const response = await fetch(url, { ...requestOptions, signal: controller.signal });
                clearTimeout(timeoutId);

                // 429 backoff
                if (response.status === 429 && attempt < this.config.retries) {
                    const retryAfter = response.headers.get('retry-after');
                    const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : this.config.retryDelay * (attempt + 1);
                    await this.delay(delay);
                    continue;
                }

                const ct = response.headers.get('content-type') || '';
                const data = ct.includes('application/json')
                    ? await response.json().catch(() => ({}))
                    : await response.text();

                if (!response.ok) {
                    const payload = typeof data === 'string' ? { error: { message: data } } : data;
                    throw new APIError(
                        payload?.error?.message || `HTTP ${response.status}`,
                        response.status,
                        payload?.error?.code,
                        payload?.error?.details
                    );
                }

                return data;
            } catch (err) {
                lastError = err;

                // Don’t retry on these
                if (err instanceof APIError) {
                    if ([400, 401, 403, 404, 422].includes(err.status)) {
                        throw err;
                    }
                }

                if (attempt < this.config.retries) {
                    await this.delay(this.config.retryDelay * (attempt + 1));
                    continue;
                }
                throw lastError;
            }
        }
    }

    // -------- API endpoints (non–App Proxy) --------

    async generateCard(form) {
        const body = await this.prepareJsonPayload(form);
        return this.request('/api/cards/generate', { method: 'POST', body });
    }

    async previewCard(form = {}) {
        const body = await this.prepareJsonPayload(form);
        return this.request('/api/cards/preview', { method: 'POST', body });
    }

    async moderateText(text, fieldName = null) {
        return this.request('/api/moderation/text', {
            method: 'POST',
            body: { text, fieldName }
        });
    }

    async convertHeic(file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.request('/api/images/convert-heic', {
            method: 'POST',
            body: formData
        });
    }

    async getTeams(teamId = null) {
        const qs = teamId ? `?id=${encodeURIComponent(teamId)}` : '';
        return this.request(`/api/teams${qs}`, { method: 'GET' });
    }

    async getCard(cardId) {
        return this.request(`/api/cards/${encodeURIComponent(cardId)}`, { method: 'GET' });
    }

    getFileUrl(type, filename) {
        // Returns a direct URL for <img src>, still guarded by your middleware
        const base = this.config.baseUrl.replace(/\/+$/, '');
        return `${base}/api/files/${encodeURIComponent(type)}/${encodeURIComponent(filename)}`;
    }

    // -------- Helpers --------

    async prepareJsonPayload(data) {
        if (data instanceof FormData) {
            const obj = {};
            for (const [k, v] of data.entries()) obj[k] = v;
            return this.normalizePayload(obj);
        }
        return this.normalizePayload({ ...data });
    }

    async normalizePayload(obj) {
        const out = {};
        for (const [k, v] of Object.entries(obj)) {
            if (v === undefined || v === null) continue;

            if (k === 'image' && (v instanceof File || v instanceof Blob)) {
                out[k] = await this.fileToBase64(v);
            } else if (typeof v === 'boolean' || typeof v === 'number' || typeof v === 'string') {
                out[k] = v;
            } else {
                out[k] = typeof v === 'object' ? JSON.stringify(v) : String(v);
            }
        }
        return out;
    }

    async fileToBase64(fileOrBlob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result; // data:*/*;base64,XXXX
                const base64 = typeof result === 'string' ? result.split(',')[1] : '';
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(fileOrBlob);
        });
    }

    validateImageFile(file, maxSize = 10 * 1024 * 1024) {
        const errors = [];
        const isValidType =
            file.type.startsWith('image/') ||
            /\.(heic|heif)$/i.test(file.name) ||
            file.type.includes('heic') ||
            file.type.includes('heif');

        if (!isValidType) errors.push('Please select an image file (JPG, PNG, HEIC, etc.)');
        if (file.size > maxSize) errors.push(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);

        return { isValid: errors.length === 0, errors };
    }

    async testImageFile(file) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(img.src);
                resolve(true);
            };
            img.onerror = () => {
                URL.revokeObjectURL(img.src);
                resolve(false);
            };
            img.src = URL.createObjectURL(file);
        });
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    configure(newConfig) {
        if (newConfig.baseUrl) newConfig.baseUrl = newConfig.baseUrl.replace(/\/+$/, '');
        this.config = { ...this.config, ...newConfig };
        return this;
    }

    getConfig() {
        return { ...this.config };
    }

    isConfigured() {
        return Boolean(this.config.baseUrl);
    }

    async testConnection() {
        try {
            await this.getTeams();
            return { success: true };
        } catch (error) {
            return { success: false, error: error?.message || 'Connection test failed' };
        }
    }
}

/** Custom API Error */
class APIError extends Error {
    constructor(message, status, code = null, details = null) {
        super(message);
        this.name = 'APIError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
    isModerationError() { return this.code === 'MODERATION_FAILED'; }
    isValidationError() { return this.code === 'VALIDATION_ERROR'; }
    isRateLimitError() { return this.status === 429; }
    isAuthError() { return this.status === 401 || this.status === 403; }
    getUserMessage() {
        switch (this.code) {
            case 'MODERATION_FAILED': return this.details?.reason || 'Content did not pass moderation';
            case 'VALIDATION_ERROR':  return this.message || 'Please check your input';
            case 'MISSING_API_KEY':
            case 'INVALID_API_KEY':  return 'Authentication required';
            case 'FORBIDDEN_ORIGIN': return 'Access not allowed from this domain';
            case 'RATE_LIMIT_EXCEEDED': return 'Too many requests. Please wait and try again.';
            default: return this.message || 'An unexpected error occurred';
        }
    }
}

/** Optional simple client-side limiter */
class ClientRateLimiter {
    constructor(maxRequests = 60, windowMs = 60000) {
        this.maxRequests = maxRequests;
        this.windowMs = windowMs;
        this.requests = [];
    }
    canMakeRequest() {
        const now = Date.now();
        this.requests = this.requests.filter((t) => now - t < this.windowMs);
        return this.requests.length < this.maxRequests;
    }
    recordRequest() {
        this.requests.push(Date.now());
    }
    getTimeToNextRequest() {
        if (this.canMakeRequest()) return 0;
        const oldest = Math.min(...this.requests);
        return this.windowMs - (Date.now() - oldest);
    }
}

// Exports
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { APIClient, APIError, ClientRateLimiter };
} else if (typeof window !== 'undefined') {
    window.APIClient = APIClient;
    window.APIError = APIError;
    window.ClientRateLimiter = ClientRateLimiter;
}

/**
 * Example:
 * const api = new APIClient({ baseUrl: 'https://YOUR_API_DOMAIN' });
 * const { token } = await fetch('https://YOUR_API_DOMAIN/api/frontend/token').then(r=>r.json()); // or let client fetch it automatically
 * const preview = await api.previewCard({ babyName: 'Ava', favoriteTeam: 'sf' });
 */
